import type { SuggestionTypeType } from "@shared/schemas/suggestion-type.schemas";
import type { ReactNode } from "react";
import type { SuggestionRendererFn } from "./suggestion-renderer.types";
import type { Suggestion } from "@shared/schemas/suggestion.schemas";

/**
 * SuggestionRendererRegistry - Central registry for suggestion renderers
 *
 * Usage:
 * 1. Register renderers at module load time
 * 2. Look up renderer at render time using getRenderer()
 *
 * Supports:
 * - Specific match: `PROFILE_BASED`
 * - Default fallback for unknown types
 */
export class SuggestionRendererRegistry {
  private static readonly registry = new Map<
    SuggestionTypeType,
    SuggestionRendererFn
  >();

  /**
   * Register a renderer for a specific suggestion type
   *
   * Type safety is enforced at registration time via generics.
   *
   * @param suggestionType - The SuggestionType
   * @param renderer - The renderer function
   */
  public static register<R extends Suggestion>(
    suggestionType: SuggestionTypeType,
    renderer: SuggestionRendererFn<R>,
  ): void {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Safe: widening from specific to base type
    this.registry.set(suggestionType, renderer as SuggestionRendererFn);
  }

  /**
   * Get renderer for a specific suggestion type
   *
   * Fallback chain:
   * 1. Specific match: `PROFILE_BASED`
   * 2. Default renderer
   *
   * @param suggestionType - The SuggestionType
   * @returns The appropriate renderer function
   */
  public static getRenderer(
    suggestionType: SuggestionTypeType,
  ): SuggestionRendererFn {
    const specific = this.registry.get(suggestionType);
    if (specific !== undefined) {
      return specific;
    }

    return defaultSuggestionRenderer;
  }

  public static hasRenderer(suggestionType: SuggestionTypeType): boolean {
    return this.registry.has(suggestionType);
  }

  public static clear(): void {
    this.registry.clear();
  }
}

/**
 * Fallback renderer used when no specific renderer is registered for a suggestion type.
 */
export const defaultSuggestionRenderer: SuggestionRendererFn = (
  suggestion: Suggestion,
): ReactNode => {
  return `Reason: ${suggestion.suggestionType}`;
};
